import { useState, useEffect, useCallback } from "react";
import { apiService } from "../services/api";
import { useWebSocket } from "./useWebSocket";

export interface AgentExecution {
  id: number;
  workflow_id: number;
  processor_type: string;
  step_id?: string;
  status: string;
  output_data: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface AgentExecutionsResponse {
  workflow_id: number;
  processor_executions: AgentExecution[];
}

export const useAgentExecutions = (workflowId: string | null) => {
  const [agentExecutions, setAgentExecutions] = useState<AgentExecution[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAgentExecutions = useCallback(async () => {
    if (!workflowId) {
      setAgentExecutions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getWorkflowAgentExecutions(workflowId);
      setAgentExecutions(response.processor_executions);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to fetch agent executions";
      setError(message);
      console.error("Error fetching agent executions:", err);
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  // Listen for real-time agent execution updates
  const { lastMessage } = useWebSocket();

  useEffect(() => {
    if (lastMessage && workflowId) {
      try {
        console.log(
          "[DEBUG] WebSocket message for workflow",
          workflowId,
          ":",
          lastMessage
        );

        // Handle agent execution updates
        if (lastMessage.type === "agent_execution_update") {
          const data = lastMessage.data as any; // Type assertion for agent execution data
          console.log("[DEBUG] Agent execution update data:", data);
          console.log(
            "[DEBUG] Comparing workflow IDs:",
            data?.workflow_id,
            "vs",
            parseInt(workflowId)
          );

          if (data?.workflow_id === parseInt(workflowId)) {
            console.log(
              "[DEBUG] Agent execution update received for workflow",
              workflowId,
              ", refetching data"
            );
            // Instead of manually updating state, just refetch the data like the refresh button does
            fetchAgentExecutions();
          }
        }
      } catch (error) {
        console.error("Error processing WebSocket message:", error);
      }
    }
  }, [lastMessage, workflowId, fetchAgentExecutions]);

  useEffect(() => {
    fetchAgentExecutions();
  }, [fetchAgentExecutions]);

  return {
    agentExecutions,
    loading,
    error,
    refetch: fetchAgentExecutions,
  };
};
